/*!
  _   _  ___  ____  ___ ________  _   _   _   _ ___
 | | | |/ _ \|  _ \|_ _|__  / _ \| \ | | | | | |_ _|
 | |_| | | | | |_) || |  / / | | |  \| | | | | || |
 |  _  | |_| |  _ < | | / /| |_| | |\  | | |_| || |
 |_| |_|\___/|_| \_\___/____\___/|_| \_|  \___/|___|

=========================================================
* Horizon UI - v1.1.0
=========================================================

* Product Page: https://www.horizon-ui.com/
* Copyright 2022 Horizon UI (https://www.horizon-ui.com/)

* Designed and Coded by Simmmple

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

// Chakra imports
import { Alert, AlertIcon, Box, Flex, Icon, SimpleGrid, Spinner, useColorModeValue } from '@chakra-ui/react';
// Custom components
import MiniCalendar from 'components/calendar/MiniCalendar';
import MiniStatistics from 'components/card/MiniStatistics';
import IconBox from 'components/icons/IconBox';
import { useClient } from 'hooks/useClient';
import { useSite } from 'hooks/useSite';
import { useForfait } from 'hooks/useForfait';
import { useIntervention } from 'hooks/useIntervention';
import { useEffect, useMemo, useState } from 'react';
import { numberToHour } from 'utils';
import { MdCheckBox, MdEuro, MdFileCopy, MdHourglassBottom } from 'react-icons/md';
import EmailsToSend from 'views/admin/default/components/EmailsToSend';
import ForfaitsHealth from 'views/admin/default/components/ForfaitsHealth';
import ForfaitsToRenew from 'views/admin/default/components/ForfaitsToRenew';
import HoursByMonth from 'views/admin/default/components/HoursByMonth';
import LowestRemaining from 'views/admin/default/components/LowestRemaining';
import RevenueByMonth from 'views/admin/default/components/RevenueByMonth';
import { computeDashboard, formatEuros, variation } from 'views/admin/default/dashboardData';

type DashboardSource = {
	forfaits: ForfaitType[];
	interventions: InterventionType[];
	clients: ClientType[];
	sites: SiteType[];
};

export default function UserReports() {
	// Chakra Color Mode
	const brandColor = useColorModeValue('brand.500', 'white');
	const boxBg = useColorModeValue('secondaryGray.300', 'whiteAlpha.100');
	const { fetch: fetchForfaits } = useForfait();
	const { fetch: fetchInterventions } = useIntervention();
	const { getClients } = useClient();
	const { getSites } = useSite();
	const [ source, setSource ] = useState<DashboardSource | null>(null);
	const [ loadError, setLoadError ] = useState(false);

	useEffect(() => {
		const fetchData = async () => {
			try {
				const [ forfaits, interventions, clients, sites ] = await Promise.all([
					fetchForfaits(),
					fetchInterventions(),
					getClients(),
					getSites()
				]);
				setSource({ forfaits, interventions, clients, sites });
			} catch (error) {
				console.error('Erreur lors du chargement du dashboard :', error);
				setLoadError(true);
			}
		};
		fetchData();
	}, [ fetchForfaits, fetchInterventions, getClients, getSites ]);

	const data = useMemo(
		() => (source ? computeDashboard(source.forfaits, source.interventions, source.clients, undefined, source.sites) : null),
		[ source ]
	);

	if (loadError) {
		return (
			<Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
				<Alert status='error' borderRadius='md'>
					<AlertIcon />
					Impossible de charger les données du dashboard. Rechargez la page.
				</Alert>
			</Box>
		);
	}
	// Les graphiques lisent leurs données au montage : ils ne s'affichent qu'une fois tout chargé
	if (!data) {
		return (
			<Flex pt={{ base: '130px', md: '80px', xl: '80px' }} minH='50vh' align='center' justify='center'>
				<Spinner size='xl' />
			</Flex>
		);
	}

	const { kpis } = data;
	const interventionsChange = variation(kpis.interventionsThisMonth, kpis.interventionsLastMonth);
	return (
		<Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
			<SimpleGrid columns={{ base: 1, md: 2, lg: 3, '2xl': 6 }} gap='20px' mb='20px'>
				<MiniStatistics
					startContent={
						<IconBox
							w='56px'
							h='56px'
							bg={boxBg}
							icon={<Icon w='32px' h='32px' as={MdEuro} color={brandColor} />}
						/>
					}
					name='À renouveler'
					value={formatEuros(kpis.renewAmount)}
				/>
				<MiniStatistics
					startContent={
						<IconBox
							w='56px'
							h='56px'
							bg={boxBg}
							icon={<Icon w='32px' h='32px' as={MdEuro} color={brandColor} />}
						/>
					}
					name='CA forfaits actifs'
					value={formatEuros(kpis.activeRevenue)}
				/>
				<MiniStatistics
					startContent={
						<IconBox
							w='56px'
							h='56px'
							bg='teal'
							icon={<Icon w='28px' h='28px' as={MdCheckBox} color='white' />}
						/>
					}
					name='Forfaits actifs'
					value={kpis.activeCount}
				/>
				<MiniStatistics
					startContent={
						<IconBox
							w='56px'
							h='56px'
							bg='orange'
							icon={<Icon w='28px' h='28px' as={MdCheckBox} color='white' />}
						/>
					}
					name='Forfaits à renouveler'
					value={kpis.renewCount}
				/>
				<MiniStatistics
					startContent={
						<IconBox
							w='56px'
							h='56px'
							bg={boxBg}
							icon={<Icon w='32px' h='32px' as={MdHourglassBottom} color={brandColor} />}
						/>
					}
					name='Heures restantes'
					value={numberToHour(kpis.remainingHours)}
				/>
				<MiniStatistics
					startContent={
						<IconBox
							w='56px'
							h='56px'
							bg={boxBg}
							icon={<Icon w='32px' h='32px' as={MdFileCopy} color={brandColor} />}
						/>
					}
					name='Interventions ce mois-ci'
					value={kpis.interventionsThisMonth}
					growth={interventionsChange !== null ? `${interventionsChange > 0 ? '+' : ''}${interventionsChange} %` : undefined}
				/>
			</SimpleGrid>

			<SimpleGrid columns={{ base: 1, md: 2, xl: 2 }} gap='20px' mb='20px'>
				<RevenueByMonth {...data.revenue} />
				<HoursByMonth {...data.hours} />
			</SimpleGrid>
			<SimpleGrid columns={{ base: 1, md: 1, xl: 2 }} gap='20px' mb='20px'>
				<SimpleGrid columns={{ base: 1, md: 2, xl: 2 }} gap='20px'>
					<LowestRemaining {...data.lowestRemaining} />
					<ForfaitsHealth {...data.health} />
				</SimpleGrid>
			</SimpleGrid>
			<SimpleGrid columns={{ base: 1, md: 1, xl: 2 }} gap='20px' mb='20px'>
				<ForfaitsToRenew rows={data.toRenew} />
				<SimpleGrid columns={{ base: 1, md: 2, xl: 2 }} gap='20px'>
					<EmailsToSend {...data.emailsToSend} />
					<MiniCalendar
						h='100%'
						minW='100%'
						selectRange={false}
						highlightedDays={data.interventionDays}
						highlightLabel='Jour avec intervention'
					/>
				</SimpleGrid>
			</SimpleGrid>
		</Box>
	);
}
